import { useLayoutEffect, useRef } from 'react'
import type { ReactNode, JSX } from 'react'

import { Sidebar, useMediaQuery, useSidebar } from '@buffer-mono/popcorn'

export const SMALL_DESKTOP_SIDEBAR_QUERY = '(max-width: 1024px)'

function ResponsiveSidebarState({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const { open, setOpen } = useSidebar()
  const isSmallViewport = useMediaQuery(SMALL_DESKTOP_SIDEBAR_QUERY)
  const wasSmallViewportRef = useRef<boolean | null>(null)
  const openBeforeSmallViewportRef = useRef(true)

  useLayoutEffect(() => {
    if (wasSmallViewportRef.current === null) {
      if (isSmallViewport && open) {
        setOpen(false)
      }
      wasSmallViewportRef.current = isSmallViewport
      return
    }

    if (isSmallViewport === wasSmallViewportRef.current) {
      return
    }

    if (isSmallViewport) {
      openBeforeSmallViewportRef.current = open
      setOpen(false)
    } else {
      setOpen(openBeforeSmallViewportRef.current)
    }

    wasSmallViewportRef.current = isSmallViewport
  }, [isSmallViewport, open, setOpen])

  return <>{children}</>
}

/**
 * Owns the responsive desktop sidebar state for Publish.
 */
export function PublishSidebarProvider({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  const isSmallViewport = useMediaQuery(SMALL_DESKTOP_SIDEBAR_QUERY)

  return (
    <Sidebar.Provider defaultOpen={!isSmallViewport}>
      <ResponsiveSidebarState>{children}</ResponsiveSidebarState>
    </Sidebar.Provider>
  )
}
